import dispatchToast from 'components/modules/Toast';
import {
  getEarthPointBalance,
  getMembershipSetting,
  getPa2PointBalance
} from 'features/membership/api';
import { EARTH_FEATURE_CODES } from 'features/membership/libs/const/plugins/earthFeatureCodes';
import { PA2_FEATURE_CODES } from 'features/membership/libs/const/plugins/pa2FeatureCodes';
import { GetMembershipSettingResponse } from 'features/membership/types';
import { API_RESPONSE_STATUS } from 'libs/constants';
import { membershipSettingAtom } from 'libs/recoil/atom';
import _ from 'lodash';
import { useState } from 'react';
import { useAsyncFn } from 'react-use';
import { useRecoilState } from 'recoil';

/**
 * ginger テンプレで GingerPointBox に渡す統一形式
 * earth / pa2 のレスポンスをこの形に変換して扱う
 */
type GingerPointBalance = {
  total_point: number;
  lapse_due_date: string;
};

type UsePointBalanceReturns = {
  pointBalanceResponseData: GingerPointBalance | undefined;
  serviceTime:
    | {
        available_hour?: string;
        unavailable_hour?: string;
      }
    | undefined;
  isMutating: boolean;
  executeGetPointBalance: () => Promise<GingerPointBalance | undefined>;
};

/**
 * ginger テンプレ用のポイント残高取得 hook
 *
 * 呼び出されると以下の流れで動作する：
 *   1. getMembershipSetting で最新のプラグイン構成を取得し、差分があれば atom を更新
 *   2. 取得した値から直接 active 判定（保存値ではなくレスポンス値を使い Recoil 更新待ちを避ける）
 *   3. pa2 active なら getPa2PointBalance、earth active なら getEarthPointBalance を直接呼ぶ
 *   4. レスポンスを GingerPointBox 互換の形式に整形して返す
 *
 * @param cardNumber 会員番号
 * @param onUnavailableTimeError earth API が503エラーした際に実行する関数（pa2 では使用しない）
 */
const usePointBalance = (
  cardNumber: string,
  onUnavailableTimeError?: VoidFunction
): UsePointBalanceReturns => {
  const [currentSetting, setMembershipSetting] = useRecoilState(
    membershipSettingAtom
  );
  const [serviceTime, setServiceTime] =
    useState<UsePointBalanceReturns['serviceTime']>();

  /**
   * 最新の features を読み込み、有効となっているpluginに差分があれば更新する関数
   * 同リクエスト内でRecoilから更新した情報を取得できないため、取得したレスポンスから 有効となっているpluginの配列を返す
   */
  const syncFeatures = async () => {
    const res = await getMembershipSetting();
    if (res.status !== API_RESPONSE_STATUS.SUCCEEDED) return null;

    if (isPluginChanged(currentSetting.plugins, res.data.plugins)) {
      setMembershipSetting(res.data);
    }
    return buildFeaturesByPlugin(res.data.plugins);
  };

  /**
   * pa2 API を呼び出し GingerPointBalance に変換して返す関数
   * エラー時は toast を表示
   */
  const fetchPa2PointBalance = async (): Promise<
    GingerPointBalance | undefined
  > => {
    const res = await getPa2PointBalance(cardNumber);
    if (res.status === API_RESPONSE_STATUS.SUCCEEDED) {
      return {
        total_point: res.data.total_point,
        lapse_due_date: res.data.expiring_date ?? ''
      };
    }
    dispatchToast({
      id: 'membership-member-api-error',
      toastText: res.errors.join('\n')
    });
  };

  /**
   * earth API を呼び出し GingerPointBalance に変換して返す関数
   * サービス時間外（503 + error_fields）は serviceTime に設定、その他エラーは toast
   */
  const fetchEarthPointBalance = async (): Promise<
    GingerPointBalance | undefined
  > => {
    const res = await getEarthPointBalance(cardNumber);
    if (res.status === API_RESPONSE_STATUS.SUCCEEDED) {
      return {
        total_point: res.data.total_point,
        lapse_due_date: res.data.lapse_due_date
      };
    }

    if (
      res.httpStatus === 503 &&
      res?.error_fields &&
      Object.keys(res.error_fields).length
    ) {
      setServiceTime({
        available_hour: res.error_fields.available_hour ?? '',
        unavailable_hour: res.error_fields.unavailable_hour ?? ''
      });
      onUnavailableTimeError?.();
      return;
    }
    dispatchToast({
      id: 'membership-member-api-error',
      toastText: res.errors.join('\n')
    });
  };

  // ===== メイン処理 =====
  // 1. 最新のプラグイン構成を取得
  // 2. pa2 / earth どちらが active かを取得値から判定
  // 3. active な側の API を呼んでポイント残高を取得（pa2 優先）
  const [
    { value: pointBalanceResponseData, loading: isMutating },
    executeGetPointBalance
  ] = useAsyncFn(async () => {
    if (!cardNumber) return;

    const features = await syncFeatures();
    if (!features) return;

    const isPa2Active = !!features.pa2?.includes(
      _.camelCase(PA2_FEATURE_CODES.GET_POINT_BALANCE)
    );
    const isEarthActive = !!features.earth?.includes(
      _.camelCase(EARTH_FEATURE_CODES.GET_POINT_BALANCE)
    );

    setServiceTime(undefined);

    if (isPa2Active) return fetchPa2PointBalance();
    if (isEarthActive) return fetchEarthPointBalance();
    // pa2 / earth のいずれも無効なら何もしない
  }, [
    cardNumber,
    currentSetting,
    setMembershipSetting,
    onUnavailableTimeError
  ]);

  return {
    pointBalanceResponseData,
    serviceTime,
    isMutating,
    executeGetPointBalance
  };
};

// ----- ヘルパ関数 -----

/**
 * plugins 配列を { alias_code: feature_codes[] } 形式の辞書に変換する
 */
const buildFeaturesByPlugin = (
  plugins: GetMembershipSettingResponse['data']['plugins']
): Record<string, string[]> =>
  plugins.reduce<Record<string, string[]>>((acc, p) => {
    acc[p.alias_code] = p.feature_codes.map((c) => _.camelCase(c));
    return acc;
  }, {});

/**
 * プラグイン構成が変化したか判定する
 */
const isPluginChanged = (
  a: GetMembershipSettingResponse['data']['plugins'],
  b: GetMembershipSettingResponse['data']['plugins']
): boolean => JSON.stringify(a) !== JSON.stringify(b);

export default usePointBalance;
